import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconTrash, IconView } from '../components/icons';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';

interface PayrollRunRow {
  id: number;
  period_start: string;
  period_end: string;
  status: 'draft' | 'finalized';
  entry_count: number;
  total_net: number;
}

const STATUS_BADGE: Record<PayrollRunRow['status'], string> = {
  draft: 'badge-scheduled',
  finalized: 'badge-active',
};

// Module-level constant so usePaginatedList sees a stable filters identity
// across renders — an inline {} literal would be a new object every render
// and defeat its "only reset to page 1 when filters actually change" check.
const NO_FILTERS = {};

function formatPeriod(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const s = new Date(start).toLocaleDateString('en-GB', opts);
  const e = new Date(end).toLocaleDateString('en-GB', { ...opts, year: 'numeric' });
  return `${s} – ${e}`;
}

const emptyForm = { period_start: '', period_end: '' };

export function Payroll() {
  const { can } = useAuth();
  const { settings } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();
  const navigate = useNavigate();

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const canCreate = can('hr.create');
  const canArchive = can('hr.archive');

  const { data, loading, error, setPage, reload } = usePaginatedList<PayrollRunRow>('/payroll-runs', NO_FILTERS);

  const fmtMoney = (n: number) => formatMoney(n, settings.currency);

  const openAdd = () => {
    setForm(emptyForm);
    setErrors({});
    setAdding(true);
  };

  const submitAdd = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const res = await api.post<{ id: number }>('/payroll-runs', form);
      setAdding(false);
      navigate(`/hr/payroll/${res.data.id}`);
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (run: PayrollRunRow) => {
    if (!(await confirmAction(`Delete this draft payroll run for ${formatPeriod(run.period_start, run.period_end)}?`))) return;
    try {
      await api.delete(`/payroll-runs/${run.id}`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Payroll</h1>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            + Add
          </button>
        )}
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Period</th>
                <th>Status</th>
                <th>Employees</th>
                <th>Total Net</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((run) => (
                  <tr key={run.id}>
                    <td>
                      <strong>{formatPeriod(run.period_start, run.period_end)}</strong>
                    </td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[run.status]}`}>{run.status}</span>
                    </td>
                    <td>{run.entry_count}</td>
                    <td>{fmtMoney(run.total_net)}</td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/hr/payroll/${run.id}`} />
                      {canArchive && run.status === 'draft' && (
                        <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(run)} />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="muted center">
                    No payroll runs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {adding && (
        <Modal title="New payroll run" onClose={() => setAdding(false)}>
          <form onSubmit={submitAdd} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Period start *
              <input
                type="date"
                value={form.period_start}
                onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                required
              />
              {errors.period_start && <small className="error">{errors.period_start[0]}</small>}
            </label>
            <label>
              Period end *
              <input
                type="date"
                value={form.period_end}
                onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                required
              />
              {errors.period_end && <small className="error">{errors.period_end[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={!form.period_start || !form.period_end}>
                Create
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
